import { useState, useMemo, useCallback } from "react";
import { PERFORMANCE_METRICS } from "@marketing-sites/constants/performance-metrics";

export interface RoiCalculatorInputs {
  conversationsPerMonth: number;
  numberOfCustomerSupportEmployees: number;
  annualCostPerEmployee: number;
  resolutionRate: number;
}

export interface RoiCalculatorConfig {
  involvementRate: number;
  monthlyResolutionRateIncrease: number;
  monthlyInvolvementRateIncrease: number;
  costPerResolution: number;
}

// Helper function for summing months by year
function sumMonths(values: number[], year: number): number {
  const startMonth = (year - 1) * 12;
  const endMonth = startMonth + 12;

  return Math.round(
    values.slice(startMonth, endMonth).reduce((accumulator, currentValue) => {
      return accumulator + currentValue;
    }, 0),
  );
}

export function useRoiCalculator(
  initialInputs?: Partial<RoiCalculatorInputs>,
  config?: Partial<RoiCalculatorConfig>,
) {
  // Parse the default resolution rate from performance metrics
  const { resolutionRate: finResolutionRateStr } = PERFORMANCE_METRICS;
  const finResolutionRate = parseInt(finResolutionRateStr.replace("%", ""));

  // Default configuration
  const calculatorConfig = useMemo(
    () => ({
      involvementRate: 100,
      monthlyResolutionRateIncrease: 0.6,
      monthlyInvolvementRateIncrease: 0,
      costPerResolution: 0.99,
      ...config,
    }),
    [config],
  );

  // State for all input fields
  const [conversationsPerMonth, setConversationsPerMonth] = useState(
    initialInputs?.conversationsPerMonth ?? 20000,
  );
  const [
    numberOfCustomerSupportEmployees,
    setNumberOfCustomerSupportEmployees,
  ] = useState(initialInputs?.numberOfCustomerSupportEmployees ?? 75);
  const [annualCostPerEmployee, setAnnualCostPerEmployee] = useState(
    initialInputs?.annualCostPerEmployee ?? 40000,
  );
  const [resolutionRate, setResolutionRate] = useState(
    initialInputs?.resolutionRate ?? finResolutionRate,
  );

  // ROI Calculation logic
  const roiCalculationResult = useMemo(() => {
    const {
      involvementRate,
      monthlyInvolvementRateIncrease,
      monthlyResolutionRateIncrease,
      costPerResolution,
    } = calculatorConfig;
    const costPerEmployeePerMonth = annualCostPerEmployee / 12;

    const resolutionRateByMonth = [];
    const involvementRateByMonth = [];
    const deflectionRateByMonth = [];
    const employeesFreedByMonth = [];
    const employeesRequiredByMonth = [];
    const employeeCostByMonth = [];
    const finResolutionsByMonth = [];
    const finCostByMonth = [];

    // Calculate 36 months of data
    for (let month = 0; month < 36; month++) {
      const currentMonthResolutionRate = Math.min(
        resolutionRate + month * monthlyResolutionRateIncrease,
        100,
      );
      resolutionRateByMonth.push(currentMonthResolutionRate);

      const currentMonthInvolvementRate = Math.min(
        involvementRate + month * monthlyInvolvementRateIncrease,
        100,
      );
      involvementRateByMonth.push(currentMonthInvolvementRate);

      const currentMonthDeflectionRate =
        (currentMonthResolutionRate / 100) *
        (currentMonthInvolvementRate / 100) *
        100;
      deflectionRateByMonth.push(currentMonthDeflectionRate);

      const currentEmployeesFreedByMonth =
        numberOfCustomerSupportEmployees * (currentMonthDeflectionRate / 100);
      employeesFreedByMonth.push(currentEmployeesFreedByMonth);

      const currentEmployeesRequired =
        numberOfCustomerSupportEmployees - currentEmployeesFreedByMonth;
      employeesRequiredByMonth.push(currentEmployeesRequired);

      const currentEmployeeCost =
        currentEmployeesRequired * costPerEmployeePerMonth;
      employeeCostByMonth.push(currentEmployeeCost);

      const currentFinResolutions =
        conversationsPerMonth * (currentMonthDeflectionRate / 100);
      finResolutionsByMonth.push(currentFinResolutions);

      const currentFinCost = currentFinResolutions * costPerResolution;
      finCostByMonth.push(currentFinCost);
    }

    // Calculate yearly totals
    const year0EmployeeCostPreFin =
      numberOfCustomerSupportEmployees * annualCostPerEmployee;
    const year1EmployeeCost = sumMonths(employeeCostByMonth, 1);
    const year2EmployeeCost = sumMonths(employeeCostByMonth, 2);
    const year3EmployeeCost = sumMonths(employeeCostByMonth, 3);
    const totalEmployeeCost =
      year1EmployeeCost + year2EmployeeCost + year3EmployeeCost;

    const year1FinCost = sumMonths(finCostByMonth, 1);
    const year2FinCost = sumMonths(finCostByMonth, 2);
    const year3FinCost = sumMonths(finCostByMonth, 3);
    const totalFinCost = year1FinCost + year2FinCost + year3FinCost;

    const year1AgentsFreed = Math.round(employeesFreedByMonth[11]);
    const year2AgentsFreed =
      Math.round(employeesFreedByMonth[23]) - year1AgentsFreed;
    const year3AgentsFreed =
      Math.round(employeesFreedByMonth[35]) -
      year1AgentsFreed -
      year2AgentsFreed;
    const totalAgentsFreed =
      year1AgentsFreed + year2AgentsFreed + year3AgentsFreed;

    const year1Savings =
      year0EmployeeCostPreFin - year1EmployeeCost - year1FinCost;
    const year2Savings =
      year0EmployeeCostPreFin - year2EmployeeCost - year2FinCost;
    const year3Savings =
      year0EmployeeCostPreFin - year3EmployeeCost - year3FinCost;
    const totalSavings = year1Savings + year2Savings + year3Savings;

    const returnOnInvestment =
      totalFinCost === 0
        ? 0
        : Math.round((totalSavings / totalFinCost) * 10000) / 100;

    return {
      year0EmployeeCostPreFin,
      year1EmployeeCost,
      year2EmployeeCost,
      year3EmployeeCost,
      totalEmployeeCost,
      year1FinCost,
      year2FinCost,
      year3FinCost,
      totalFinCost,
      year1AgentsFreed,
      year2AgentsFreed,
      year3AgentsFreed,
      totalAgentsFreed,
      year1Savings,
      year2Savings,
      year3Savings,
      totalSavings,
      returnOnInvestment,
    };
  }, [
    conversationsPerMonth,
    numberOfCustomerSupportEmployees,
    annualCostPerEmployee,
    resolutionRate,
    calculatorConfig,
  ]);

  return {
    // Current values
    conversationsPerMonth,
    numberOfCustomerSupportEmployees,
    annualCostPerEmployee,
    resolutionRate,
    finResolutionRate,

    // Setters with validation
    setConversationsPerMonth: useCallback((value: number) => {
      setConversationsPerMonth(Math.max(0, value));
    }, []),
    setNumberOfCustomerSupportEmployees: useCallback((value: number) => {
      setNumberOfCustomerSupportEmployees(Math.max(0, value));
    }, []),
    setAnnualCostPerEmployee: useCallback((value: number) => {
      setAnnualCostPerEmployee(Math.max(0, value));
    }, []),
    setResolutionRate: useCallback((value: number) => {
      setResolutionRate(Math.min(100, Math.max(1, value)));
    }, []),

    // Calculated results
    roiCalculationResult,
  };
}

export type RoiCalculationResult = ReturnType<
  typeof useRoiCalculator
>["roiCalculationResult"];
